/**
 * Cyber insurance directory data. Source: docs/research/cyber-insurers.json (read from each carrier's
 * own site, verified 2026-11-03). Ranking basis: NAIC 2026 Report on the Cybersecurity Insurance
 * Market (2025 data), Table 1 Top 20 Groups, plus well-known SMB-facing brands. Phones/emails appear
 * only where read from the carrier's own page; otherwise null. Hiscox, Hartford, Zurich, Hanover,
 * Sompo, QBE, Vouch, Allied World and Embroker have no verified hotline here. COUNSEL REVIEW before
 * launch: accuracy/staleness, implied endorsement, inclusion of MGAs.
 */
export interface InsurerListing {
  name: string; aliases: string[]; type: "Carrier" | "MGA"; parentGroup: string | null
  claimsUrl: string | null; phone: string | null; phoneNote: string | null; claimsEmail: string | null
  instructions: string | null; panelNote: string | null; quoteUrl: string | null; verifiedOn: string
}
export const INSURERS_VERIFIED_ON = "2026-11-03"
export const INSURERS: InsurerListing[] = [
  {
    "name": "AIG (CyberEdge)",
    "aliases": [
      "American International Group",
      "CyberEdge",
      "National Union Fire Insurance Company of Pittsburgh",
      "Lexington Insurance"
    ],
    "type": "Carrier",
    "parentGroup": "American Intrnl Grp",
    "claimsUrl": "https://www.aig.com/home/claims/report-a-claim/financial-lines-cyber-technology-and-media",
    "phone": "800.CYBR.345 (800.292.7345)",
    "phoneNote": null,
    "claimsEmail": "c-Claim@aig.com",
    "instructions": "Page: contact the CyberEdge Hotline 'in advance of the submission of the First Notice of Loss'; submit First Notice of Loss by email to c-Claim@aig.com.",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Allied World (Fairfax Financial)",
    "aliases": [
      "Allied World Assurance",
      "AWAC",
      "Allied World Insurance Company",
      "Fairfax"
    ],
    "type": "Carrier",
    "parentGroup": "Fairfax Financial",
    "claimsUrl": "https://alliedworldinsurance.com/general-claims/",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": "noticeofloss@awac.com",
    "instructions": "Page: 'Claims and/or notices of potential claims can be sent to Allied World via noticeofloss@awac.com'; 'providing notice to your agent/broker is not considered notice to Allied World.' No phone on page.",
    "panelNote": "No cyber or vendor mention on the page read.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Arch Insurance",
    "aliases": [
      "Arch Capital",
      "Arch Insurance Company",
      "Arch Cyber",
      "CyPro",
      "BreachAlert"
    ],
    "type": "Carrier",
    "parentGroup": "Arch Ins Grp",
    "claimsUrl": "https://insurance.archgroup.com/wp-content/uploads/sites/2/Arch105_L5_CyberInsClaimsGuide_090523.pdf",
    "phone": "1-833-923-ARCH (2724)",
    "phoneNote": null,
    "claimsEmail": "cyberclaims@archinsurance.com",
    "instructions": "Arch's US Cyber Insurance Claims Guide: contact Arch Cyber Claims 'to report a claim or incident' at the email or phone. Guide lists forensic firms' hotlines.",
    "panelNote": "Guide lists forensic/incident response firms with their own hotlines.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "At-Bay",
    "aliases": [
      "At-Bay Insurance Services",
      "At-Bay Specialty Insurance Company"
    ],
    "type": "MGA",
    "parentGroup": "At Bay Specialty Ins Co",
    "claimsUrl": "https://www.at-bay.com/claims/",
    "phone": "650-850-5408",
    "phoneNote": null,
    "claimsEmail": "claims@at-bay.com",
    "instructions": "Page: for ransomware or system interruption needing immediate attention 'call the At-Bay breach coach at 650-850-5408 or notify claim to claims@at-bay.com'.",
    "panelNote": "Page lists partner firms (AllClearID, Arete, BakerHostetler, Kroll, Unit 42); says claims 'will contact policyholders to determine which, if any, partners are needed.'",
    "quoteUrl": "https://www.at-bay.com/insurance/cyber/",
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "AXA XL",
    "aliases": [
      "XL Insurance",
      "XL Catlin",
      "AXA XL Cyber",
      "CyberRiskConnect",
      "AXA"
    ],
    "type": "Carrier",
    "parentGroup": "AXA Ins Grp",
    "claimsUrl": "https://axaxl.com/insurance/claims",
    "phone": "855-566-4724",
    "phoneNote": null,
    "claimsEmail": "webfnol.na@axaxl.com",
    "instructions": null,
    "panelNote": "Not read.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "AXIS Insurance",
    "aliases": [
      "AXIS Capital",
      "AXIS Incident Commander",
      "AXIS Safe Room"
    ],
    "type": "Carrier",
    "parentGroup": "AXIS Capital Grp",
    "claimsUrl": "https://www.axiscapital.com/insurance/cyber-technology-e-o/cyber-incident-services",
    "phone": null,
    "phoneNote": "AXIS lists an Incident Commander line that its own page says is not claim notice. Report claims through your agent, broker, or AXIS claims page.",
    "claimsEmail": "IC@axiscapital.com",
    "instructions": null,
    "panelNote": "Safe Room lets users 'Pre-assign preferred incident response vendors'; vendor names not listed.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Beazley",
    "aliases": [
      "Beazley Breach Response",
      "BBR",
      "Beazley Insurance Company",
      "Lloyd's syndicates 2623/623"
    ],
    "type": "Carrier",
    "parentGroup": "Beazley Grp",
    "claimsUrl": "https://www.beazley.com/en-150/cyber-customer-centre/report-a-cyber-incident/",
    "phone": "+1 866 567 8570",
    "phoneNote": null,
    "claimsEmail": "bbr.claims@beazley.com",
    "instructions": "Page offers an online incident form, and a regional directory listing the US phone and email. Old URL beazley.com/beazley_breach_response_claims.html redirects to an unrelated forms page.",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Berkley Cyber Risk Solutions",
    "aliases": [
      "Berkley Cyber Risk",
      "Berkley Cyber Risk Protect",
      "CyberClick",
      "WR Berkley"
    ],
    "type": "MGA",
    "parentGroup": "W. R. Berkley Corporation",
    "claimsUrl": "https://berkleycyberrisk.com/claims/",
    "phone": "973.355.8201",
    "phoneNote": null,
    "claimsEmail": "claims@berkleycyberrisk.com",
    "instructions": "Claims page: 'Send Notice of Data Security Events, Cyber Claims, Losses, and Circumstances.' Contact page shows the phone with hours 'Open 24/7'; labelled as the office number, not expressly a breach hotline.",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Berkshire Hathaway Specialty Insurance",
    "aliases": [
      "BHSI",
      "National Indemnity group",
      "Berxi"
    ],
    "type": "Carrier",
    "parentGroup": "Berkshire Hathaway",
    "claimsUrl": "https://www.bhspecialty.com/claims/",
    "phone": "855.453.9675",
    "phoneNote": "General claims line, not cyber-specific (per the carrier's site).",
    "claimsEmail": null,
    "instructions": "Claims page lists a '24-hour toll-free number' under 'All other claims'; email address obfuscated. No cyber-specific channel on page.",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Chubb",
    "aliases": [
      "Chubb Cyber ERM",
      "Federal Insurance Company",
      "Chubb Cyber Alert"
    ],
    "type": "Carrier",
    "parentGroup": "Chubb Ltd Grp",
    "claimsUrl": "https://www.chubb.com/us-en/business-insurance/products/cyber-insurance/cyber-incident-response-solutions.html",
    "phone": "800-817-2665",
    "phoneNote": null,
    "claimsEmail": "cyberclaimreport@chubb.com",
    "instructions": "Page lists 800-817-2665 as 24/7 line to a cyber incident response coach; commercial cyber claims: 'Email cyberclaimreport@chubb.com'; Chubb Cyber Alert app. Chubb personal insurance is a different line (1-800-CLAIMS-0).",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "CNA",
    "aliases": [
      "CNA Financial",
      "Continental Casualty Company",
      "CNA Cyber",
      "Hardy"
    ],
    "type": "Carrier",
    "parentGroup": "CNA Ins Grp",
    "claimsUrl": "https://www.cna.com/cyber-vendor-support",
    "phone": "800-247-3968",
    "phoneNote": null,
    "claimsEmail": "CyberIntake@cna.com",
    "instructions": "Page: report a new cyber issue by calling the CNA Cyber Hotline or emailing CyberIntake@cna.com.",
    "panelNote": "Page lists named breach counsel, forensic, notification and PR vendors; says 'CNA's Claims team will work with you to choose the vendors that best fit your particular needs.' Whether use is mandatory was not stated on the page.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Coalition",
    "aliases": [
      "Coalition Insurance Solutions",
      "Coalition Insurance Company",
      "Coalition Incident Response",
      "Allianz Global Risks US",
      "Fireman's Fund Indemnity"
    ],
    "type": "MGA",
    "parentGroup": "Coalition Inc.",
    "claimsUrl": "https://www.coalitioninc.com/report-a-claim",
    "phone": "+1 (833) 866-1337",
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": null,
    "panelNote": "Refers to affiliate 'Coalition Incident Response'; no outside panel listed on page.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Corvus by Travelers",
    "aliases": [
      "Corvus Insurance",
      "Corvus Smart Cyber",
      "Accredited Specialty Insurance Company"
    ],
    "type": "MGA",
    "parentGroup": "Travelers",
    "claimsUrl": "https://www.corvusinsurance.com/submit-a-claim",
    "phone": "1.800.842.8496",
    "phoneNote": null,
    "claimsEmail": "BSIClaims@travelers.com",
    "instructions": "Page presents the same Travelers Bond & Specialty claim options (agent, email, phone 24 hours, fax, mail); 'filed immediately after a known or suspected cyber security breach'.",
    "panelNote": "No vendors named on the page.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Cowbell",
    "aliases": [
      "Cowbell Insurance",
      "Cowbell Prime",
      "Cowbell Cyber, Inc."
    ],
    "type": "MGA",
    "parentGroup": "Cowbell Cyber",
    "claimsUrl": "https://cowbell.insure/claims-services/",
    "phone": "(833) 633-8666",
    "phoneNote": null,
    "claimsEmail": "claims@cowbellcyber.ai",
    "instructions": "Page: contact the team even if unsure you have a claim; have policy number or company name, incident date and description, and a main point of contact ready.",
    "panelNote": "Page lists panel members/partners including Arete, Booz Allen Hamilton, Constangy, Experian, Mullen Coughlin, Wilson Elser among others.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Elpha Secure",
    "aliases": [
      "Elpha"
    ],
    "type": "MGA",
    "parentGroup": "Elpha Secure",
    "claimsUrl": "https://www.elphasecure.com/insurance/claims",
    "phone": "1 (844) 964-3719",
    "phoneNote": null,
    "claimsEmail": "claims@elphasecure.com",
    "instructions": "Page: calling the incident counsel number 'does not constitute as a notice of a claim'; 'A written notice of a claim or incident must be reported to claims@elphasecure.com in accordance with the policy.'",
    "panelNote": "Page: 'only expenses incurred through the engagement of the listed Response Providers and with Elpha Secure's prior written consent will qualify as Incident Response Expenses'; providers 'encouraged'.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Embroker",
    "aliases": [
      "Embroker Insurance Services"
    ],
    "type": "MGA",
    "parentGroup": "Embroker",
    "claimsUrl": "https://www.embroker.com/claims/",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": "Page: for a cyber-related threat event 'please call the support number/hotline listed on your policy'; start non-urgent claims at https://app.embroker.com/claims/filing-list. General inquiries 1.844.436.2765 (Mon-Fri).",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "ERGO NEXT Insurance (NEXT)",
    "aliases": [
      "NEXT Insurance",
      "Next Insurance",
      "ERGO NEXT",
      "Munich Re"
    ],
    "type": "MGA",
    "parentGroup": "ERGO Group / Munich Re",
    "claimsUrl": "https://www.nextinsurance.com/claim-page/",
    "phone": "855-222-5919",
    "phoneNote": "General claims line, not cyber-specific (per the carrier's site).",
    "claimsEmail": null,
    "instructions": "Page: log in, Claims tab, 'File a claim'; non-customers 'Call our claims team' or use the online form. General (not cyber-specific) claims line.",
    "panelNote": null,
    "quoteUrl": "https://www.nextinsurance.com/cyber-liability-insurance/",
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Great American Insurance Group (Cyber Risk)",
    "aliases": [
      "Great American",
      "GAIG",
      "Risk-e-Business"
    ],
    "type": "Carrier",
    "parentGroup": "Great American Insurance Group (American Financial Group)",
    "claimsUrl": "https://www.greatamericaninsurancegroup.com/about-us/business-operations/claims/cyber-risk",
    "phone": "877-209-2009",
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": "Page: use listed contact information; '24/7/365 access to respond to your cyber risk emergencies, with access to a suite of vendors.'",
    "panelNote": "Mentions 'a suite of vendors', none named.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Hiscox",
    "aliases": [
      "Hiscox Insurance Company Inc.",
      "Hiscox USA",
      "Hiscox CyberClear"
    ],
    "type": "Carrier",
    "parentGroup": "Hiscox Ltd",
    "claimsUrl": "https://www.hiscox.com/manage-your-policy/claims-center",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": null,
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Liberty Mutual (incl. Ironshore)",
    "aliases": [
      "Liberty Mutual Insurance",
      "Liberty Surplus",
      "Ironshore",
      "Liberty Mutual Business Insurance"
    ],
    "type": "Carrier",
    "parentGroup": "Liberty Mut Grp",
    "claimsUrl": "https://business.libertymutual.com/claims/report-a-claim/",
    "phone": "1-833-517-0815",
    "phoneNote": "Ironshore policies may list a different number. Check your policy.",
    "claimsEmail": "cyberclaims@libertymutual.com",
    "instructions": "Under 'Liberty Mutual or Ironshore specialty': 'Call our 24/7 cyber hotline: 1-833-517-0815'; submit by email; cyber response mobile app. 'Please review your policy's claim and notice reporting form prior to submitting a claim.'",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Markel",
    "aliases": [
      "Markel Insurance",
      "Markel American Insurance Company",
      "Evanston Insurance Company"
    ],
    "type": "Carrier",
    "parentGroup": "Markel Group",
    "claimsUrl": "https://www.markel.com/claims/report-a-claim",
    "phone": "1.844.462.7535",
    "phoneNote": null,
    "claimsEmail": "newclaims@markel.com",
    "instructions": "Page lists 'Cyber Response Hotline' 1.844.462.7535; general claims +1.800.362.7535 / newclaims@markel.com. Hours not stated.",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Nationwide (E&S / Enterprise Cyber)",
    "aliases": [
      "Nationwide E&S/Specialty",
      "Nationwide Mutual",
      "Scottsdale Insurance Company"
    ],
    "type": "Carrier",
    "parentGroup": "Nationwide",
    "claimsUrl": "https://www.nationwide.com/business/insurance/management-liability-specialty/products/cyber-and-professional-liability/",
    "phone": "1-888-275-7992",
    "phoneNote": null,
    "claimsEmail": "mlsreportaloss@nationwide.com",
    "instructions": "Page: '24/7 claims reporting is available at 1-888-275-7992 or mlsreportaloss@nationwide.com.' (Enterprise Cyber product page.)",
    "panelNote": "Lists 'Breach Remediation Partners' categories (breach coach, forensics, notification) without names.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "QBE",
    "aliases": [
      "QBE North America",
      "QBE Insurance Corporation"
    ],
    "type": "Carrier",
    "parentGroup": "QBE Ins Grp Ltd",
    "claimsUrl": "https://www.qbe.com/us/policyholders/claims/businesses-organizations-report-a-new/report-new-cyber-claim-event",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": null,
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Resilience",
    "aliases": [
      "Cyber Resilience",
      "Resilience Insurance"
    ],
    "type": "MGA",
    "parentGroup": "Resilience Cyber Insurance Solutions",
    "claimsUrl": "https://cyberresilience.com/file-a-claim",
    "phone": "1-(302) 722-7236",
    "phoneNote": null,
    "claimsEmail": "claims@resilienceinsurance.com",
    "instructions": "Emergencies: call the hotline (US/CA); non-emergency claims by email.",
    "panelNote": "No panel names on page.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Sompo International",
    "aliases": [
      "Sompo",
      "Endurance",
      "Sompo America Insurance Company"
    ],
    "type": "Carrier",
    "parentGroup": "Sompo Grp",
    "claimsUrl": null,
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": null,
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Starr Insurance",
    "aliases": [
      "Starr Companies",
      "Starr Indemnity & Liability Company",
      "Starr Surplus Lines Insurance Company"
    ],
    "type": "Carrier",
    "parentGroup": "Starr Grp",
    "claimsUrl": "https://starrcompanies.com/Client-Services/Claims",
    "phone": "1.855.STARR25 (1.855.782.7725)",
    "phoneNote": "General claims line, not cyber-specific (per the carrier's site).",
    "claimsEmail": "Claims@starrcompanies.com",
    "instructions": "General Starr claims page; no cyber-specific instructions on it. Cyber product pages returned 404.",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "The Cincinnati Insurance Company",
    "aliases": [
      "Cincinnati Insurance Companies",
      "Cincinnati Cyber Defense",
      "Data Defender"
    ],
    "type": "Carrier",
    "parentGroup": "Cincinnati Financial",
    "claimsUrl": "https://www.cinfin.com/contact-us/claims",
    "phone": "877-242-2544",
    "phoneNote": "General claims line, not cyber-specific (per the carrier's site).",
    "claimsEmail": null,
    "instructions": "General claims page: 'Call 877-242-2544 anytime, day or night, to report claims.' Online claim submission link on page. No cyber-specific instructions.",
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "The Hanover",
    "aliases": [
      "Hanover Insurance Company",
      "Citizens Insurance Company of America"
    ],
    "type": "Carrier",
    "parentGroup": "The Hanover Insurance Group",
    "claimsUrl": "https://www.hanover.com/businesses/products/cyber",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": "Cyber page mentions 'dedicated cyber claim reporting' but gives no contact details on the text read.",
    "panelNote": "Page: 'Our panel of cyber service providers are ready to provide trusted guidance and support' (no names).",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "The Hartford",
    "aliases": [
      "Hartford Fire Insurance Company",
      "CyberChoice",
      "CyberChoice First Response",
      "FirstResponse Hotline"
    ],
    "type": "Carrier",
    "parentGroup": "Hartford Fire & Cas Grp",
    "claimsUrl": "https://www.thehartford.com/cyber-insurance",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": null,
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Tokio Marine HCC",
    "aliases": [
      "TMHCC",
      "HCC",
      "Houston Casualty Company",
      "U.S. Specialty Insurance Company",
      "NAS Insurance",
      "Cyber & Professional Lines Group",
      "CPLG"
    ],
    "type": "Carrier",
    "parentGroup": "Tokio Marine Holdings Inc Grp",
    "claimsUrl": "https://www.tmhcc.com/en-us/claims/cyber-and-professional-lines-claims",
    "phone": "888.627.8995",
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": "Page: cyber policyholders 'email [address hidden in fetched text] or call 888.627.8995'; other TMHCC lines 800.742.2210; review policy or contact broker.",
    "panelNote": "Names no panel; says claims examiners coordinate 'legal counsel, as needed.'",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Travelers",
    "aliases": [
      "The Travelers Companies",
      "Travelers Bond & Specialty Insurance",
      "Travelers CyberRisk",
      "St. Paul Fire and Marine",
      "Travelers Casualty and Surety"
    ],
    "type": "Carrier",
    "parentGroup": "St Paul Travelers Grp",
    "claimsUrl": "https://www.travelers.com/claims/file-claim/business/cyber",
    "phone": "1.800.842.8496",
    "phoneNote": null,
    "claimsEmail": "BSIClaims@travelers.com",
    "instructions": null,
    "panelNote": "Page does not mention vendors or a panel.",
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Vouch",
    "aliases": [
      "Vouch Insurance Services"
    ],
    "type": "MGA",
    "parentGroup": "Vouch Insurance",
    "claimsUrl": "https://support.vouch.us/hc/en-us/articles/4498698227099-How-do-I-file-a-claim-or-incident",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": null,
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  },
  {
    "name": "Zurich North America",
    "aliases": [
      "Zurich American Insurance Company",
      "Zurich Cyber Insurance Policy",
      "SpearTip"
    ],
    "type": "Carrier",
    "parentGroup": "Zurich Ins Grp",
    "claimsUrl": "https://www.zurichna.com/insurance/cyber/cyber-event-resources",
    "phone": null,
    "phoneNote": null,
    "claimsEmail": null,
    "instructions": null,
    "panelNote": null,
    "quoteUrl": null,
    "verifiedOn": "2026-11-03"
  }
]
